import { useCallback, useEffect, useState } from 'react'
import { Icon } from './Icon'
import { api, RequestFailed, type TrashedPage } from '../api'
import { useDialog } from '../useDialog'

/**
 * The trash: what was deleted, and the way back.
 *
 * Pages and shared blocks both, because both are a writer's words and both are
 * gone from nowhere else when they were written and deleted on the same draft.
 * A row says which kind it is, since a block has no title to show and its path
 * is the only thing that tells them apart.
 *
 * Deleting used to be the one edit with no undo, which is why the inspector's
 * button now says what it really does — move to trash — and why this panel
 * exists. Three decisions in it:
 *
 * Restore needs a draft, because a restore is a staged change like any other
 * edit. With none open the button says so instead of failing, and the person
 * keeps their place.
 *
 * A restore whose address is taken now is a QUESTION, not an error: the server
 * refuses with a suggested free address, and the person confirms or types a
 * better one. Nothing is ever overwritten.
 *
 * Delete forever is confirmed in words, inline, the same shape the rollback
 * confirmation uses — it is the one genuinely irreversible action here.
 */
interface Props {
  open: boolean
  onClose: () => void
  /** The draft a restore lands on. Null when reading the live site. */
  branch: string | null
  /** Whether this person may write at all; a viewer sees the list only. */
  mayWrite: boolean
  /** Owner/admin only: deleting FOREVER removes everybody's safety net. */
  mayPurge: boolean
  /** So the shell can refresh the tree and open the page that came back. */
  onRestored: (path: string, placed: boolean) => void
}

/** "3 minutes ago". Absolute dates make a reader do arithmetic. */
function ago(iso: string): string {
  const seconds = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000)
  const scale: [number, string][] = [
    [60, 'second'],
    [60, 'minute'],
    [24, 'hour'],
    [7, 'day'],
    [4.35, 'week'],
    [12, 'month'],
  ]
  let value = seconds
  let unit = 'second'
  for (const [size, next] of scale) {
    if (value < size) break
    value /= size
    unit = next
  }
  const rounded = Math.floor(value)
  if (unit === 'second' && rounded < 30) return 'just now'
  return `${rounded} ${unit}${rounded === 1 ? '' : 's'} ago`
}

export function TrashDrawer({ open, onClose, branch, mayWrite, mayPurge, onRestored }: Props) {
  const panel = useDialog<HTMLElement>(open, onClose)
  const [pages, setPages] = useState<TrashedPage[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  /** The row whose "delete forever" is being confirmed. Null means none is. */
  const [confirming, setConfirming] = useState<string | null>(null)
  /**
   * The row whose address is taken, and what it would be restored under.
   *
   * This was a `window.prompt`, which is the operating system asking a
   * question about a row nobody can see while it is up — the address it is
   * complaining about is behind the box. It is the same question the row
   * beneath already asks about deleting for good, so it is asked the same way:
   * inside the row, where the path it conflicts with is still on the screen.
   */
  const [renaming, setRenaming] = useState<{ id: string; taken: string; to: string } | null>(null)

  const load = useCallback(async () => {
    try {
      const body = await api.trash()
      setPages(body.pages)
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }, [])

  useEffect(() => {
    if (open) void load()
    else {
      setConfirming(null)
      setRenaming(null)
      setError(null)
    }
  }, [open, load])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      // Escape backs out of whatever is open inside a row first. Closing the
      // whole panel would lose the place somebody was looking at.
      if (renaming) setRenaming(null)
      else if (confirming) setConfirming(null)
      else onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose, confirming, renaming])

  const restore = async (row: TrashedPage, toPath?: string) => {
    if (!branch) return
    setBusy(true)
    setError(null)
    try {
      const result = await api.restoreTrash(row.id, branch, toPath)
      setPages((prev) => prev?.filter((p) => p.id !== row.id) ?? prev)
      setRenaming(null)
      onRestored(result.path, result.placed)
    } catch (e) {
      if (e instanceof RequestFailed && e.code === 'slug_taken') {
        /**
         * The address is taken, and the decision is the person's. The field
         * arrives pre-filled with the server's suggestion so accepting it is
         * one keystroke — but whatever they type goes back as an explicit
         * choice, which is the only thing the server accepts past a conflict.
         */
        const suggestion =
          typeof e.body['suggestion'] === 'string' ? e.body['suggestion'] : `${row.path}`
        setRenaming({ id: row.id, taken: row.path, to: suggestion })
        return
      }
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const deleteForever = async (row: TrashedPage) => {
    setBusy(true)
    setError(null)
    try {
      await api.deleteTrash(row.id)
      setPages((prev) => prev?.filter((p) => p.id !== row.id) ?? prev)
      setConfirming(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  if (!open) return null

  return (
    <div className="scrim" onMouseDown={onClose}>
      <aside
        className="drawer"
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label="Deleted pages and blocks"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="drawer__head">
          <div>
            <h2 className="drawer__title">Trash</h2>
            <p className="drawer__sub">
              Deleted pages and shared blocks stay here for 30 days, then they are removed for good.
            </p>
          </div>
          <button className="btn btn--ghost btn--icon" onClick={onClose}>
            <Icon name="close" size={15} label="Close" />
          </button>
        </header>

        <div className="drawer__body">
          {error && <p className="panel__error">{error}</p>}

          {/* Held while it is read, for the reason the sentence below is
              worded so carefully: a blank panel and "there is nothing in the
              trash" look the same, and only one of them is a claim. */}
          {pages === null && !error && (
            <div className="screen__loading" aria-busy="true">
              <p className="sr-only">Loading…</p>
              <span className="skeleton" />
              <span className="skeleton" />
              <span className="skeleton" />
            </div>
          )}

          {/* No action here, deliberately.
              Every other empty surface in the writer offers the one obvious
              next act. An empty trash has none: it is filled by deleting a
              page, and offering that would be inviting somebody to destroy
              work so that a panel has something in it. The sentence says what
              the place is FOR, and that is the whole answer. */}
          {pages !== null && pages.length === 0 && (
            <p className="nothing">
              Nothing is in the trash. When a page or a shared block is deleted it lands here, and
              it can be put back onto a draft from this panel.
            </p>
          )}

          <ol className="pubs">
            {(pages ?? []).map((row) => (
              <li key={row.id} className="pubs__row">
                <span className="pubs__mark" aria-hidden="true">
                  <Icon name="trash" size={13} />
                </span>
                <div className="pubs__what">
                  <div className="pubs__title truncate">
                    {row.title ?? row.path}
                    {row.path.startsWith('snippets/') && (
                      <span className="badge badge--quiet">shared block</span>
                    )}
                  </div>
                  <div className="pubs__meta">
                    {row.deletedByName ?? 'somebody'} · {ago(row.deletedAt)} · on{' '}
                    {row.branch.replace(/^draft\//, '')}
                  </div>
                  <div className="pubs__paths">{row.path}</div>

                  {mayWrite && confirming !== row.id && renaming?.id !== row.id && (
                    <div className="pubs__actions">
                      <button
                        className="btn btn--tinted btn--sm"
                        disabled={busy || !branch}
                        onClick={() => void restore(row)}
                        data-tip={
                          branch
                            ? 'Put the page back onto this draft, as a staged change'
                            : 'Open a draft first — a restore is an edit, and the live site is read only'
                        }
                      >
                        <Icon name="undo" size={12} />
                        Restore
                      </button>
                      {mayPurge && (
                        <button
                          className="btn btn--ghost btn--sm"
                          disabled={busy}
                          onClick={() => setConfirming(row.id)}
                          data-tip="Remove it from the trash for good"
                        >
                          <Icon name="trash" size={12} />
                          Delete forever
                        </button>
                      )}
                    </div>
                  )}

                  {renaming?.id === row.id && (
                    <div className="pubs__confirm">
                      <p className="drawer__hint">
                        Another page lives at {renaming.taken} now. Restore this one under a
                        different address:
                      </p>
                      <form
                        onSubmit={(e) => {
                          e.preventDefault()
                          const to = renaming.to.trim()
                          if (to) void restore(row, to)
                        }}
                      >
                        <input
                          className="input mono"
                          autoFocus
                          aria-label="The address to restore this page under"
                          value={renaming.to}
                          disabled={busy}
                          onChange={(e) =>
                            setRenaming((prev) => (prev ? { ...prev, to: e.target.value } : prev))
                          }
                        />
                        <div className="pubs__actions">
                          <button
                            className="btn btn--tinted btn--sm"
                            disabled={busy || renaming.to.trim() === ''}
                          >
                            <Icon name="undo" size={12} />
                            Restore here
                          </button>
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            disabled={busy}
                            onClick={() => setRenaming(null)}
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    </div>
                  )}

                  {confirming === row.id && (
                    <div className="pubs__confirm">
                      <p className="drawer__hint">
                        This removes the page from the trash for good. There is no way back from
                        this one.
                      </p>
                      <div className="pubs__actions">
                        <button
                          className="btn btn--danger btn--sm"
                          disabled={busy}
                          onClick={() => void deleteForever(row)}
                        >
                          Delete forever
                        </button>
                        <button
                          className="btn btn--ghost btn--sm"
                          disabled={busy}
                          onClick={() => setConfirming(null)}
                        >
                          Keep it
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </div>
      </aside>
    </div>
  )
}
